/**
 * Klaviyo "Search Query" metric (TWS-1378).
 *
 * One event per retail search, sent from the page that search actually opens.
 * Prefixbox can send /search?q=… on to a collection; that collection URL is the
 * Search URL. A search that stays on /search uses the search URL.
 *
 * Sending (identify vs guest anonymous_id) lives in @theme/metric-transport,
 * shared with the Product Viewed metric. Do not rename this file to
 * klaviyo-events.js; that name is in the perf-defer-script blocklist.
 *
 * The IIFE stays inside the module so the early bails below can use `return`.
 */
import { trackKlaviyoEvent } from '@theme/metric-transport';

(function () {
  const STORAGE_KEY = 'tjc:klaviyo-search-query';
  const PENDING_MAX_AGE_MS = 60000;
  const SEARCH_FALLBACK_MS = 15000;
  const AFTER_PREFIXBOX_MS = 3000;
  const METRIC_NAME = 'Search Query';

  const body = document.body;
  if (!body) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (body.dataset.storefrontSearchScope === 'auction') return;

  const origin = (body.dataset.klaviyoSearchOrigin || window.location.origin).replace(/\/$/, '');
  const pageType = body.dataset.klaviyoSearchPage || '';

  /**
   * @typedef {Object} SearchQueryState
   * @property {string} term - Last submitted retail search term
   * @property {number} at - When it was submitted (ms)
   * @property {boolean} sawSearch - The /search page for that term was reached
   * @property {boolean} userNavigated - Shopper clicked away from /search themselves
   * @property {string} sentTerm - Term already sent to Klaviyo
   */

  /** @type {number | undefined} */
  let searchSendTimer;
  /** @type {AbortController | null} */
  let searchCountController = null;
  /** @type {Promise<number | null> | null} */
  let searchCountPromise = null;
  let searchCountTerm = '';
  let prefixboxWaitArmed = false;
  /** @type {MutationObserver | null} */
  let scriptObserver = null;

  /**
   * @returns {SearchQueryState}
   */
  function readState() {
    const empty = { term: '', at: 0, sawSearch: false, userNavigated: false, sentTerm: '' };

    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return empty;

      const parsed = JSON.parse(raw);
      return {
        term: typeof parsed.term === 'string' ? parsed.term : '',
        at: typeof parsed.at === 'number' ? parsed.at : 0,
        sawSearch: parsed.sawSearch === true,
        userNavigated: parsed.userNavigated === true,
        sentTerm: typeof parsed.sentTerm === 'string' ? parsed.sentTerm : '',
      };
    } catch {
      return empty;
    }
  }

  /**
   * @param {SearchQueryState} state
   */
  function writeState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* Private mode / quota: skip the metric rather than throw. */
    }
  }

  /**
   * @param {string} pathname
   * @returns {boolean}
   */
  function isSearchPath(pathname) {
    return /\/search\/?$/.test(pathname || '');
  }

  /**
   * @returns {boolean}
   */
  function isSearchPage() {
    return pageType === 'search' || isSearchPath(window.location.pathname);
  }

  /**
   * @param {string} href
   * @returns {string}
   */
  function searchTermFromUrl(href) {
    try {
      const url = new URL(href, window.location.origin);
      if (!isSearchPath(url.pathname)) return '';
      return (url.searchParams.get('q') || '').trim();
    } catch {
      return '';
    }
  }

  /**
   * @returns {string}
   */
  function currentQuery() {
    return searchTermFromUrl(window.location.href);
  }

  /**
   * @param {string} path
   * @returns {string}
   */
  function productionUrl(path) {
    try {
      return `${origin}${new URL(path, origin).pathname}`;
    } catch {
      return '';
    }
  }

  /**
   * @param {string} term
   * @returns {string}
   */
  function searchResultsUrl(term) {
    return `${origin}/search?q=${encodeURIComponent(term)}`;
  }

  /**
   * @param {string} term
   * @param {string} url
   * @param {number} count
   */
  function sendOnce(term, url, count) {
    if (!term || !url || !Number.isInteger(count) || count < 0) return;

    const state = readState();
    if (state.sentTerm === term) return;

    trackKlaviyoEvent(METRIC_NAME, {
      'Search Term': term,
      'Search URL': url,
      'Search Result Count': count,
    });

    writeState({ ...state, term, sentTerm: term, userNavigated: false });
  }

  /**
   * Full Shopify search total; the visible SERP only holds one page of cards.
   * @param {string} term
   * @returns {Promise<number | null>}
   */
  function fetchSearchResultCount(term) {
    if (searchCountPromise && searchCountTerm === term) return searchCountPromise;

    searchCountController?.abort();
    const controller = new AbortController();
    searchCountController = controller;
    searchCountTerm = term;

    const url = new URL('/search', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'section-rendering-search-analytics');

    searchCountPromise = (async () => {
      try {
        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) return null;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const payload = doc.querySelector('[data-gtm-search-analytics]');
        if (!payload?.textContent) return null;

        const count = Number(JSON.parse(payload.textContent)?.search_results_count);
        return Number.isInteger(count) && count >= 0 ? count : null;
      } catch {
        return null;
      }
    })();

    return searchCountPromise;
  }

  /**
   * The total Prefixbox renders ("4 Products"). On /search that node is
   * `.pfbx-controll-right-side` (Prefixbox spells it "controll"). The theme
   * toolbar class is only on collection pages. Shopify's own count is capped
   * at 1000 and does not match what the shopper sees.
   * @returns {number | null}
   */
  function visibleResultCount() {
    const node =
      document.querySelector('.pfbx-controll-right-side') ||
      document.querySelector('.main-collection__results-count-number');
    if (!node) return null;

    const count = Number.parseInt((node.textContent || '').replace(/[^\d]/g, ''), 10);
    return Number.isInteger(count) && count >= 0 ? count : null;
  }

  /**
   * @returns {Promise<void>}
   */
  async function sendSearchPageEvent() {
    const term = currentQuery();
    if (!term || readState().sentTerm === term) return;

    const visible = visibleResultCount();
    const count = visible == null ? await fetchSearchResultCount(term) : visible;
    if (count == null || currentQuery() !== term) return;

    sendOnce(term, searchResultsUrl(term), count);
  }

  function clearSearchSendTimer() {
    if (searchSendTimer === undefined) return;
    window.clearTimeout(searchSendTimer);
    searchSendTimer = undefined;
  }

  /**
   * @param {number} delay
   */
  function armSearchSend(delay) {
    clearSearchSendTimer();
    searchSendTimer = window.setTimeout(() => {
      scriptObserver?.disconnect();
      scriptObserver = null;
      void sendSearchPageEvent();
    }, delay);
  }

  function onPrefixboxLoaded() {
    if (prefixboxWaitArmed) return;
    prefixboxWaitArmed = true;

    // The integration script loads before the toolbar is painted. Sending on
    // that load falls through to Shopify's capped count.
    if (visibleResultCount() == null) return;

    armSearchSend(AFTER_PREFIXBOX_MS);
  }

  /**
   * Prefixbox is parked by perf-defer-script until first interaction, so its
   * redirect can happen late. Shorten the wait once its script has loaded.
   * @param {Node} node
   */
  function watchPrefixboxScript(node) {
    if (!(node instanceof HTMLScriptElement)) return;
    if (!node.src || !/prefixbox/i.test(node.src)) return;
    node.addEventListener('load', onPrefixboxLoaded, { once: true });
  }

  function bindPrefixboxWatch() {
    for (const script of document.querySelectorAll('script[src]')) {
      watchPrefixboxScript(script);
    }

    scriptObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          watchPrefixboxScript(node);
        }
      }
    });

    scriptObserver.observe(document.head, { childList: true });
    scriptObserver.observe(body, { childList: true });
  }

  /**
   * A navigation the shopper started (clicking a product or link) means the
   * search page is where the search ended, so send its URL now. A navigation
   * nobody clicked is a Prefixbox redirect; leave it to the destination page.
   */
  function bindSearchPageLeaving() {
    document.addEventListener(
      'click',
      (event) => {
        if (!event.isTrusted || event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (!(event.target instanceof Element)) return;

        const link = event.target.closest('a[href]');
        if (!(link instanceof HTMLAnchorElement) || link.target === '_blank') return;
        if (searchTermFromUrl(link.href) === currentQuery()) return;

        writeState({ ...readState(), userNavigated: true });
        clearSearchSendTimer();
        void sendSearchPageEvent();
      },
      true
    );

    window.addEventListener('pagehide', (event) => {
      if (event.persisted) return;

      clearSearchSendTimer();
      scriptObserver?.disconnect();
      scriptObserver = null;

      if (!readState().userNavigated) searchCountController?.abort();
    });
  }

  function setupSearchPage() {
    const term = currentQuery();
    if (!term) return;

    const state = readState();
    const sameTerm = state.term === term;
    writeState({
      term,
      at: sameTerm && state.at ? state.at : Date.now(),
      sawSearch: true,
      userNavigated: false,
      sentTerm: sameTerm ? state.sentTerm : '',
    });

    if (sameTerm && state.sentTerm === term) return;

    void fetchSearchResultCount(term);
    armSearchSend(SEARCH_FALLBACK_MS);
    bindPrefixboxWatch();
    bindSearchPageLeaving();
  }

  /**
   * Only a collection reached straight from a pending search sends; menu visits
   * and later browsing carry no stored term or are already sent.
   */
  function setupCollectionPage() {
    const state = readState();
    if (!state.term || state.sentTerm === state.term || state.userNavigated) return;
    if (!state.at || Date.now() - state.at > PENDING_MAX_AGE_MS) return;

    const referrerTerm = searchTermFromUrl(document.referrer || '');
    if (referrerTerm ? referrerTerm !== state.term : !state.sawSearch) return;

    const count = Number.parseInt(body.dataset.klaviyoSearchCount || '', 10);
    sendOnce(state.term, productionUrl(body.dataset.klaviyoSearchPath || ''), count);
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.querySelector('[name="ra_q"]')) return;
      if (!(form.getAttribute('action') || '').includes('/search')) return;

      const input = form.querySelector('input[name="q"]');
      if (!(input instanceof HTMLInputElement)) return;

      const term = input.value.trim();
      if (!term) return;

      writeState({ term, at: Date.now(), sawSearch: false, userNavigated: false, sentTerm: '' });
    },
    true
  );

  if (pageType === 'collection') {
    setupCollectionPage();
  } else if (isSearchPage()) {
    setupSearchPage();
  }
})();
